import * as Accordion from '@radix-ui/react-accordion'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { ApiError, getProduct } from '../api/client'
import type { Product } from '../api/types'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { Gallery } from '../components/Gallery'
import { Fehlerhinweis } from '../components/States'
import { VariantPicker } from '../components/VariantPicker'
import { Zeichen } from '../components/Zeichen'
import { useCart } from '../cart'
import { useResource } from '../hooks/useResource'
import { useSeo } from '../hooks/useSeo'
import { useSprache, useT } from '../i18n'
import { usePriceFormat, useShopConfig } from '../shopConfig'
import { useShopPage } from '../shopPages'
import { ersteAuswahl, findeVariante, preisSpanne, waehle, type Auswahl } from '../variants'
import { NotFoundPage } from './NotFoundPage'

/** Ab wann „nur noch wenige" dransteht. */
const KNAPP_AB = 5

export function ProductPage() {
  const t = useT()
  const { handle = '' } = useParams()
  const { shopName, currency, primaryHost } = useShopConfig()
  const preis = usePriceFormat()

  const { data: produkt, error, loading } = useResource((signal) => getProduct(handle, signal), [handle])

  const [auswahl, setAuswahl] = useState<Auswahl>({})

  // Die Vorauswahl entsteht erst, wenn das Produkt da ist. An der handle-Kette
  // aufgehängt, nicht am Objekt: Sonst setzte jede neue Antwort die Auswahl
  // des Kunden zurück.
  useEffect(() => {
    if (produkt) setAuswahl(ersteAuswahl(produkt))
  }, [produkt])

  const variante = produkt ? findeVariante(produkt, auswahl) : null
  const spanne = produkt ? preisSpanne(produkt) : null

  useSeo({
    // Produktname und SEO-Text kommen aus der Kasse und bleiben, wie sie sind.
    title: produkt
      ? t('produkt.seoTitel', { name: produkt.seoTitle || produkt.name, laden: shopName })
      : t('produkt.seoTitelLeer', { laden: shopName }),
    description: produkt?.seoDescription || produkt?.description?.slice(0, 155) || undefined,
    canonicalHost: primaryHost,
  })

  if (error instanceof ApiError && error.isNotFound) {
    return <NotFoundPage was={t('produkt.nichtGefunden')} />
  }
  if (error != null) return <Fehlerhinweis fehler={error} />

  if (loading || !produkt) {
    return (
      <div className="sf-produkt">
        <div className="sf-galerie">
          <div className="sf-galerie-bild skelett" style={{ borderRadius: 'var(--radius-flaeche)' }} />
        </div>
        <div className="sf-kauf">
          <div className="skelett skelett-zeile" style={{ width: '70%', height: '2.2rem' }} />
          <div className="skelett skelett-zeile" style={{ width: '30%', height: '1.6rem' }} />
          <div className="skelett skelett-zeile" style={{ width: '90%' }} />
        </div>
      </div>
    )
  }

  const anzeigePreis = variante
    ? preis(variante.price)
    : spanne
      ? spanne.min === spanne.max
        ? preis(spanne.min)
        : t('produkt.abPreis', { preis: preis(spanne.min) })
      : null

  return (
    <>
      <Breadcrumbs pfade={[{ name: t('nav.startseite'), pfad: '/' }, { name: produkt.name }]} />

      <div className="sf-produkt">
        <Gallery bilder={produkt.images} produktName={produkt.name} />

        {/* Der Kaufbereich bleibt auf dem Rechner beim Rollen stehen: Wer sich
            durch fünf Bilder arbeitet, soll den Knopf nicht wiederfinden
            müssen. Sticky und nicht fixed — wird er länger als das Fenster,
            rollt er mit, statt sich abzuschneiden. */}
        <div className="sf-kauf">
          <div className="sf-kauf-kopf">
            <h1>{produkt.name}</h1>
            {/* aria-live, weil sich der Preis mit der Variante ändert — wer
                nicht hinsieht, bekäme das sonst nicht mit. */}
            <p className="sf-preis" aria-live="polite">
              <strong>{anzeigePreis ?? t('produkt.preisAufAnfrage')}</strong>
              <span>{t('produkt.steuerhinweis')}</span>
            </p>
          </div>

          <hr className="sf-trenner" />

          <VariantPicker
            product={produkt}
            auswahl={auswahl}
            onWaehle={(achse, wert) => setAuswahl(waehle(produkt, auswahl, achse, wert))}
          />

          <Verfuegbarkeit produkt={produkt} variante={variante} />

          <InDenWarenkorb variante={variante} />

          <Hinweise />

          <Einzelheiten beschreibung={produkt.description} />
        </div>
      </div>

      <StrukturierteDaten produkt={produkt} currency={currency} />
    </>
  )
}

function Verfuegbarkeit({
  produkt,
  variante,
}: {
  produkt: Product
  variante: ReturnType<typeof findeVariante>
}) {
  const t = useT()

  // Keine Variante zur Auswahl kann nach waehle eigentlich nicht vorkommen —
  // außer das Backend liefert Achsen ohne passende Variante. Dann lieber eine
  // ehrliche Meldung als ein leerer Absatz.
  if (!variante) {
    return (
      <p className="sf-lieferbar" data-stand="weg" aria-live="polite">
        {t('produkt.keineZusammenstellung')}
      </p>
    )
  }

  if (!variante.available) {
    return (
      <p className="sf-lieferbar" data-stand="weg" aria-live="polite">
        {t('produkt.ausverkauft')}
        {produkt.variantAxes.length > 0 && t('produkt.andereAuswahl')}
      </p>
    )
  }

  const knapp = variante.stockQuantity > 0 && variante.stockQuantity <= KNAPP_AB

  return (
    <p className="sf-lieferbar" data-stand={knapp ? 'knapp' : 'da'} aria-live="polite">
      {t('produkt.vorraetig')}
      {knapp && t('produkt.nurNoch', { anzahl: variante.stockQuantity })}
    </p>
  )
}

/**
 * Menge und Kaufknopf in einer Zeile (Shopify Horizon, Hers).
 *
 * Die Menge vorher zu wählen spart den Umweg über den Warenkorb für jeden, der
 * zwei Gläser Honig will. Sie ist nach oben begrenzt, wenn der Bestand geführt
 * wird: Mehr anzubieten, als da ist, hieße, im Korb gleich wieder zu kürzen.
 *
 * Der Knopf ist gesperrt statt versteckt, wenn die Variante ausverkauft ist:
 * Ein verschwundener Knopf sieht aus wie eine kaputte Seite, ein gesperrter
 * sagt mit seiner Beschriftung, warum gerade nichts geht.
 *
 * In den Korb geht ausschließlich die Variantenkennung und die Menge. Preis
 * und Name holt der Warenkorb selbst vom Server.
 */
function InDenWarenkorb({ variante }: { variante: ReturnType<typeof findeVariante> }) {
  const { t, tz } = useSprache()
  const { ansicht, arbeitet, hinzufuegen, oeffnen } = useCart()
  const [menge, setMenge] = useState(1)

  // Eine andere Variante fängt wieder bei eins an: Drei Stück in M heißt nicht
  // drei Stück in L.
  useEffect(() => {
    setMenge(1)
  }, [variante?.id])

  if (!variante) return null

  const imKorb = ansicht.lines.find((zeile) => zeile.variantId === variante.id)?.quantity ?? 0
  // stockQuantity 0 bei lieferbarer Variante heißt „nicht bestandsgeführt" —
  // dann gibt es keine Obergrenze.
  const geführt = variante.available && variante.stockQuantity > 0
  const nochFrei = geführt ? Math.max(0, variante.stockQuantity - imKorb) : Infinity
  const grenzeErreicht = geführt && nochFrei === 0
  const hoechstens = Math.min(nochFrei, 99)

  return (
    <>
      <div className="sf-kaufzeile">
        <div className="sf-menge" role="group" aria-label={t('produkt.menge')}>
          <button
            type="button"
            aria-label={t('produkt.mengeWeniger')}
            disabled={menge <= 1}
            onClick={() => setMenge((m) => Math.max(1, m - 1))}
          >
            <Zeichen art="minus" groesse={16} />
          </button>
          <output aria-live="polite">{menge}</output>
          <button
            type="button"
            aria-label={t('produkt.mengeMehr')}
            disabled={menge >= hoechstens}
            onClick={() => setMenge((m) => Math.min(hoechstens, m + 1))}
          >
            <Zeichen art="plus" groesse={16} />
          </button>
        </div>

        <button
          className="sf-knopf sf-knopf-haupt"
          type="button"
          /* Solange der Warenkorb antwortet, kein zweiter Klick: Sonst legt ein
             ungeduldiger Doppelklick das Doppelte hinein. */
          disabled={!variante.available || grenzeErreicht || arbeitet}
          onClick={() => {
            void hinzufuegen(variante.id, Math.min(menge, hoechstens)).then(() => setMenge(1))
            // Die Schublade als Bestätigung: Ein Knopf, der nach dem Klick
            // aussieht wie vorher, lässt den Kunden ein zweites Mal drücken.
            oeffnen()
          }}
        >
          {!variante.available
            ? t('produkt.ausverkauft')
            : grenzeErreicht
              ? t('korb.mehrNichtVorraetig')
              : arbeitet
                ? t('korb.wirdHinzugefuegt')
                : t('korb.inDenWarenkorb')}
        </button>
      </div>

      {imKorb > 0 && (
        <p className="sf-imkorb" role="status">
          {tz(imKorb, 'korb.liegtEins', 'korb.liegenViele')}{' '}
          <button type="button" onClick={oeffnen}>
            {t('korb.ansehen')}
          </button>
        </p>
      )}
    </>
  )
}

/**
 * Versand und Abholung — mit den Zahlen, nach denen die Kasse rechnet.
 *
 * Von lululemon: Wie die Ware zum Kunden kommt, steht auf der Produktseite und
 * nicht erst an der Kasse. Die Zusagen der Startseite sind Freitext des
 * Händlers; hier stehen die eingestellten Werte, damit Seite und Kasse nie
 * auseinanderlaufen.
 */
function Hinweise() {
  const t = useT()
  const preis = usePriceFormat()
  const { shippingFlatGross = 0, shippingFreeFrom = 0, pickupEnabled = false } = useShopConfig()

  const versand =
    shippingFlatGross <= 0
      ? t('produkt.versandFrei')
      : shippingFreeFrom > 0
        ? t('produkt.versandMitGrenze', { preis: preis(shippingFlatGross), grenze: preis(shippingFreeFrom) })
        : t('produkt.versandPauschal', { preis: preis(shippingFlatGross) })

  return (
    <ul className="sf-hinweise">
      <li>
        <Zeichen art="paket" groesse={20} />
        <div>
          <strong>{t('produkt.versandTitel')}</strong>
          <span>{versand}</span>
        </div>
      </li>
      {pickupEnabled && (
        <li>
          <Zeichen art="laden" groesse={20} />
          <div>
            <strong>{t('produkt.abholungTitel')}</strong>
            <span>{t('produkt.abholungText')}</span>
          </div>
        </li>
      )}
    </ul>
  )
}

/**
 * Beschreibung, Versand und Rückgabe zum Aufklappen.
 *
 * Die Beschreibung ist offen: Sie ist der Grund, aus dem jemand die Seite
 * liest. Versand und Rückgabe sind zu — sie sind wichtig, aber erst, wenn
 * jemand danach sucht, und dann findet er sie unter ihrem Namen.
 */
function Einzelheiten({ beschreibung }: { beschreibung: string }) {
  const t = useT()
  const versand = useShopPage('versand')
  const widerruf = useShopPage('widerruf')

  const eintraege: { wert: string; titel: string; inhalt: JSX.Element }[] = []

  if (beschreibung.trim()) {
    eintraege.push({
      wert: 'beschreibung',
      titel: t('produkt.beschreibung'),
      inhalt: (
        <>
          {beschreibung.split('\n\n').map((absatz, i) => (
            <p key={i}>{absatz}</p>
          ))}
        </>
      ),
    })
  }

  if (versand || widerruf) {
    eintraege.push({
      wert: 'versand',
      titel: t('produkt.versandRueckgabe'),
      inhalt: (
        <>
          <p>{t('produkt.versandRueckgabeText')}</p>
          <p>
            {versand && <Link to={`/seite/${versand.handle}`}>{versand.title}</Link>}
            {versand && widerruf && ' · '}
            {widerruf && <Link to={`/seite/${widerruf.handle}`}>{widerruf.title}</Link>}
          </p>
        </>
      ),
    })
  }

  if (eintraege.length === 0) return null

  return (
    <Accordion.Root
      className="sf-details"
      type="multiple"
      defaultValue={eintraege[0]?.wert === 'beschreibung' ? ['beschreibung'] : []}
    >
      {eintraege.map((eintrag) => (
        <Accordion.Item className="sf-details-eintrag" value={eintrag.wert} key={eintrag.wert}>
          <Accordion.Header className="sf-details-kopf">
            <Accordion.Trigger className="sf-details-knopf">
              {eintrag.titel}
              <Zeichen art="runter" groesse={18} />
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content className="sf-details-inhalt">
            <div>{eintrag.inhalt}</div>
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  )
}

/**
 * schema.org-Angaben für Suchmaschinen.
 *
 * Preise als Bruttowerte, unverändert aus der Antwort: Was in der Suche als
 * Preis erscheint, muss dem entsprechen, was auf der Seite steht — sonst
 * fliegt der Laden aus den Ergebnissen.
 */
function StrukturierteDaten({ produkt, currency }: { produkt: Product; currency: string }) {
  const daten = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: produkt.name,
    description: produkt.seoDescription || produkt.description,
    image: produkt.images.map((bild) => bild.url),
    offers: produkt.variants.map((variante) => ({
      '@type': 'Offer',
      price: variante.price,
      priceCurrency: currency || 'EUR',
      availability: variante.available
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
    })),
  }

  return (
    <script
      type="application/ld+json"
      // Spitze Klammern maskiert: Ein Produktname mit „</script>" würde das
      // Dokument sonst an dieser Stelle aufbrechen.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(daten).replace(/</g, '\\u003c') }}
    />
  )
}
